/**
 * Lightweight client-side PDF complexity check for NewProjectPage.
 * Flags likely image-heavy / scanned PDFs before the translator commits to
 * a project, since these currently produce degraded segmentation output.
 * This is a heuristic warning only — it does not parse or fix the PDF.
 */

const LOW_TEXT_CHARS_PER_PAGE = 150;
const IMAGE_PER_PAGE_THRESHOLD = 1;

export interface PdfComplexityResult {
    complex: boolean;
    reason: string;
}

export async function checkPdfComplexity(
    file: File,
): Promise<PdfComplexityResult | null> {
    try {
        const pdfjsLib = await import("pdfjs-dist");
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        let totalChars = 0;
        let imageCount = 0;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);

            const textContent = await page.getTextContent();
            totalChars += textContent.items.reduce(
                (sum: number, item) =>
                    sum + ("str" in item ? item.str.length : 0),
                0,
            );

            const opList = await page.getOperatorList();
            imageCount += opList.fnArray.filter(
                (fn) =>
                    fn === pdfjsLib.OPS.paintImageXObject ||
                    fn === pdfjsLib.OPS.paintInlineImageXObject ||
                    fn === pdfjsLib.OPS.paintJpegXObject,
            ).length;
        }

        const avgCharsPerPage = totalChars / pdf.numPages;
        const imagesPerPage = imageCount / pdf.numPages;

        if (avgCharsPerPage < LOW_TEXT_CHARS_PER_PAGE) {
            return {
                complex: true,
                reason: "low extractable text relative to page count — likely scanned or image-based",
            };
        }
        if (imagesPerPage > IMAGE_PER_PAGE_THRESHOLD) {
            return {
                complex: true,
                reason: "heavy use of embedded images or complex layout",
            };
        }
        return { complex: false, reason: "" };
    } catch (err) {
        console.warn("PDF complexity pre-check failed:", err);
        return null;
    }
}
